import EventEmitter from 'eventemitter3';
import { useEffect, useState } from 'react';
import Big from 'big.js';
import { formatValue } from '@bitbot/shared/utils/quote-value';

export const eventEmitter = new EventEmitter();

export const useWindowWidth = () => {
  const [clientWidth, setClientWidth] = useState<number>(() => {
    try {
      return window ? window.document.body.clientWidth : 1000;
    } catch {
      return 1000;
    }
  });

  const setWidth = () => {
    setClientWidth(window.document.body.clientWidth);
  };

  useEffect(() => {
    if (!window) return;

    window.addEventListener('resize', setWidth);

    return () => {
      window.removeEventListener('resize', setWidth);
    };
  }, []);

  return clientWidth;
};

export const useIsMobile = () => useWindowWidth() < 768;

export const formatNumber = (value: number | undefined | '') => {
  if (value === undefined || value === '') return '';
  return new Intl.NumberFormat('en-US').format(value);
};

export const formatQuoteValue = (value: number) => {
  const displayStr = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumSignificantDigits: 3,
  }).format(value);

  return displayStr;
};

// shorten the checksummed version of the input address to have 4 characters at start and end
export function shortenAddress(address: string, chars = 6): string {
  return `${address?.slice(0, chars)}...${address?.slice(-chars + 2)}`;
}

export function shortenAddressV2(
  address: string,
  start = 4,
  end = 4,
): string {
  return `${address?.slice(0, start)}...${address?.slice(-end)}`;
}

export function getFontFamilyVariable() {
  const container = document.querySelector('#main');
  if (!container) return '';
  const containerStyles = getComputedStyle(container);
  const fontFamily = containerStyles
    .getPropertyValue('--font-manrope')
    .trim();
  return fontFamily;
}

export function formatDigit(number: number) {
  if (Number.isNaN(number)) return '0';
  return new Big(number).prec(8).toString();
}

export function formatPrice(number: number) {
  if (Number.isNaN(number)) return 0;
  if (number < 1) {
    return +new Big(number).toPrecision(2);
  } else {
    const fixed = number.toFixed(2);
    const num = +fixed * 1000;
    return Math.round(num) / 1000;
  }
}
export function formatEnglishNumber(num: number, showK = true) {
  let _num = 0;
  if (num >= 10 ** 15) {
    _num = Math.floor((num / 10 ** 15) * 100) / 100;
    if (num > 999) return '>999Q';
    return formatNumber(_num) + 'Q';
  } else if (num >= 10 ** 12) {
    _num = Math.floor((num / 10 ** 12) * 100) / 100;
    return formatNumber(_num) + 'T';
  } else if (num >= 1000000000) {
    _num = Math.floor((num / 1000000000) * 100) / 100;
    return formatNumber(_num) + 'B';
  } else if (num >= 1000000) {
    _num = Math.floor((num / 1000000) * 100) / 100;
    return formatNumber(_num) + 'M';
  }
  // else if (num >= 1000) {
  //   _num = Math.floor((num / 1000) * 100) / 100;
  //   return formatNumber(_num) + 'K';
  // }
  else {
    if (num > 1) {
      if (showK) {
        return new Intl.NumberFormat('en-US', {
          notation: 'compact',
          maximumFractionDigits: 2,
        }).format(num);
      } else {
        return formatNumber(formatPrice(num));
      }
    } else {
      return formatValue(num);
    }
  }
}

function isSmallIOS() {
  return (
    typeof navigator !== 'undefined' &&
    /iPhone|iPod/.test(navigator.userAgent)
  );
}
function isLargeIOS() {
  return (
    typeof navigator !== 'undefined' && /iPad/.test(navigator.userAgent)
  );
}
export function isIOS() {
  return isSmallIOS() || isLargeIOS();
}

export async function writeTextToClipboard(
  text: string,
  success?: () => void,
) {
  try {
    if (window?.DexJsBridge) {
      window.DexJsBridge.copyText(text);
    } else {
      await navigator.clipboard.writeText(text);
    }
  } catch {}
  if (success) {
    success();
  }
}

export function formatInputNumber(number: string) {
  return number
    .replace(/,/, '.')
    .replace(/，/, '.')
    .replace(/[^0-9.]/g, '')
    .replace(/(\..*?)\..*/g, '$1');
}
